import { computed, toValue } from 'vue';

import {
  str as StrLib,
  SpotType,
  SpotStatus,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  AssetContainerType
} from '@nova/core';
import { useYardSpotGridPresenterV2 } from '@satellite/composables/useYardSpotGridPresenterV2';
import { toISpot, useYardSpotStatus } from '@satellite/composables/useYardSpotStatus';

import type { ISpot } from '@nova/core';
import type { MaybeRefOrGetter, ComputedRef } from 'vue';
import type { SpotGridItem } from '@satellite/composables/useYardSpotGridPresenterV2';

interface IGroupedSpot {
  code: string;
  id: string;
  icon: string;
  group: string;
  status: string;
  disabled: boolean;
  raw: ISpot;
}

type IGroupedItem = { header: string } | IGroupedSpot;

/**
 * Options for useYardSpotSelect composable.
 *
 * `warehouseId` wants a getter, not a value: a Pinia store is `reactive`, so
 * `store.selectedWarehouseId` reads as a plain string and passing it directly would pin the picker
 * to whatever warehouse was selected when the dialog mounted — and these dialogs never unmount.
 */
interface IUseYardSpotSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null>;
  includeClosedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssignedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeReservedSpots?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<boolean | 'disabled'>;
  assetType?: MaybeRefOrGetter<AssetContainerType | null>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardSpotSelect {
  spotsByAreaRef: ComputedRef<IGroupedItem[]>;
  isLoadingRef: ComputedRef<boolean>;
  hasNoSpotsRef: ComputedRef<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Composable for spot selection functionality.
 *
 * Areas, spots, assignments and the open move tasks holding each spot all come down together from
 * the `yard/view/spot-grid` BFF, so the picker never stitches task refs client-side. Filtering,
 * search and grouping stay local because the endpoint returns the whole option set.
 */
export function useYardSpotSelect(options: IUseYardSpotSelectOptions = {}): IYardSpotSelect {
  const {
    warehouseId,
    includeClosedSpots = false,
    includeAssignedSpots = false,
    includeReservedSpots = false,
    includeAssociatedToActiveMoveTasks = false,
    assetType = null,
    search = '',
    showDisabled = true
  } = options;

  // `headless`: the dialog is mounted for the whole authenticated session (see `App.vue`), so there
  // is no viewport to gate the fetch on.
  const {
    data: gridAreas,
    isLoading,
    refresh
  } = useYardSpotGridPresenterV2({
    warehouseId: computed(() => toValue(warehouseId) || null),
    headless: true
  });

  const spotStatus = useYardSpotStatus();

  /**
   * Get spot icon based on spot type.
   */
  const getSpotIcon = (type: string): string => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  /**
   * The spot's status as a bare key, not a label: this composable has no i18n context.
   *
   * The keys match `core.yard.spotStatuses` one for one, in all four locales, so a consumer
   * resolves the label under that prefix — `EventTriggeredSpotSelectDialog` does exactly that.
   */
  const getSpotStatusKey = (spot: SpotGridItem): string => {
    if (spotStatus.isSpotOpen(spot)) {
      return 'open';
    }

    if (spotStatus.isSpotClosed(spot)) {
      return 'closed';
    }

    if (spotStatus.isSpotAssigned(spot)) {
      return 'assigned';
    }

    if (spot.status === SpotStatus.RESERVED_MANUAL) {
      return 'reservedManual';
    }

    if (spot.status === SpotStatus.RESERVED_AUTOMATED) {
      return 'reservedAutomated';
    }

    return 'unknown';
  };

  /**
   * Check if asset should be excluded based on filters
   */
  const shouldExcludeClosedSpot = (spot: SpotGridItem): boolean => spotStatus.isSpotClosed(spot);

  const shouldExcludeReservedSpot = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotReserved(spot);

  const shouldExcludeAssignedSpot = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotAssigned(spot);

  /**
   * Whether an open move task holds this spot, as resolved server-side: the task either targets the
   * spot, or moves the container currently parked on it.
   */
  const shouldExcludeSpotAssociatedToActiveMoveTask = (spot: SpotGridItem): boolean =>
    spotStatus.isSpotInActiveMoveTask(spot);

  /**
   * Check if spot should be disabled based on asset type requirements
   */
  const shouldDisableByAssetType = (spot: SpotGridItem): boolean => {
    const assetTypeValue = toValue(assetType);

    if (!assetTypeValue || spotStatus.isSpotOpen(spot)) {
      return false;
    }

    const spotAsISpot = toISpot(spot);

    if (assetTypeValue === AssetContainerType.Trailer) {
      // For assetVisit: spot must have assetContainer, but not assetVisit
      return (
        !isSpotAssignedToAssetContainer(spotAsISpot) || isSpotAssignedToAssetVisit(spotAsISpot)
      );
    }

    if (assetTypeValue === AssetContainerType.Container) {
      // For assetContainer: spot must not have assetContainer
      return isSpotAssignedToAssetContainer(spotAsISpot);
    }

    return false;
  };

  /**
   * Check if spot should be disabled (included but not selectable)
   */
  const shouldDisableSpot = (spot: SpotGridItem): boolean => {
    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const includeClosedDisabled =
      includeClosedValue === 'disabled' && spotStatus.isSpotClosed(spot);
    const includeAssignedDisabled =
      includeAssignedValue === 'disabled' && spotStatus.isSpotAssigned(spot);
    const includeReservedDisabled =
      includeReservedValue === 'disabled' && spotStatus.isSpotReserved(spot);
    const includeMoveTaskDisabled =
      includeMoveTaskValue === 'disabled' && spotStatus.isSpotInActiveMoveTask(spot);

    return (
      includeClosedDisabled ||
      includeAssignedDisabled ||
      includeReservedDisabled ||
      includeMoveTaskDisabled ||
      shouldDisableByAssetType(spot)
    );
  };

  const groupedSpotsByArea = computed((): IGroupedItem[] => {
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    const includeClosedValue = toValue(includeClosedSpots);
    const includeAssignedValue = toValue(includeAssignedSpots);
    const includeReservedValue = toValue(includeReservedSpots);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const exclusions = [
      ...(includeClosedValue ? [] : [shouldExcludeClosedSpot]),
      ...(includeReservedValue ? [] : [shouldExcludeReservedSpot]),
      ...(includeAssignedValue ? [] : [shouldExcludeAssignedSpot]),
      ...(includeMoveTaskValue ? [] : [shouldExcludeSpotAssociatedToActiveMoveTask])
    ];

    const isExcludedBySomeFilter = (spot: SpotGridItem): boolean =>
      exclusions.some(exclude => exclude(spot));

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const items: IGroupedItem[] = [];

    for (const area of gridAreas.value) {
      const matchesAreaName = matchesSearchString(area.name);
      const matchesSearch = (spot: SpotGridItem): boolean =>
        !searchQuery || matchesSearchString(spot.code) || matchesAreaName;

      const rows: IGroupedSpot[] = (area.spots ?? [])
        .filter(spot => !isExcludedBySomeFilter(spot) && matchesSearch(spot))
        .map(spot => ({
          code: spot.code,
          id: spot.id,
          icon: getSpotIcon(spot.type),
          group: area.name,
          status: getSpotStatusKey(spot),
          disabled: shouldDisableSpot(spot),
          raw: toISpot(spot)
        }))
        .filter(row => showDisabledValue || !row.disabled)
        .sort((a, b) => StrLib.compareAlphaNumeric(a.code, b.code));

      if (rows.length > 0) {
        items.push({ header: area.name.toUpperCase() });
        items.push(...rows);
      }
    }

    return items;
  });

  const hasNoSpots = computed(() => !isLoading.value && groupedSpotsByArea.value.length === 0);

  return {
    spotsByAreaRef: groupedSpotsByArea,
    isLoadingRef: isLoading,
    hasNoSpotsRef: hasNoSpots,

    refresh
  };
}
